import { memo, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type AirActionTrackingLocation, useTrackCopiedShareLink } from '@air/analytics';
import { ShortUrlObjectType } from '@air/api/types';
import { EllipsisVerticalIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { DropdownMenu, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';
import { formatFullName } from '@air/utilities';

import { NoPermissionToCreateShareLink } from '~/components/NoPermissionToCreateShareLink';
import { CopyLinkButton } from '~/components/ShareModal/components/ShareLink/CopyLinkButton';
import { ReadonlyShareLink } from '~/components/ShareModal/components/ShareLink/ReadonlyShareLink';
import { ShareLinkMenuFooter } from '~/components/ShareModal/components/ShareLink/ShareLinkMenuFooter';
import { ShareLinkModalFooter } from '~/components/ShareModal/components/ShareLinkModalFooter';
import { ShareLinkModal } from '~/components/ShareModal/ShareLinkModal';
import { SharePrivateCanvasModalBody } from '~/components/SharePrivateCanvasModal/SharePrivateCanvasModalBody';
import { SharePrivateCanvasRegenerate } from '~/components/SharePrivateCanvasModal/SharePrivateCanvasRegenerate';
import { SharePrivateContentSpinner } from '~/components/SharePrivateContentModal/SharePrivateContentSpinner';
import { SharePrivateCreateLinkError } from '~/components/SharePrivateContentModal/SharePrivateCreateLinkError';
import { NEW_TAB } from '~/constants/urls';
import { useShowTurnOnShareLinkNotifications } from '~/hooks/turnOnEmailShareNotificationsToast/useShowTurnOnShareLinkNotifications';
import { useShowShareLinkTeamInviteNudgeForModal } from '~/hooks/useShowShareLinkTeamInviteNudgeForModal';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useWorkspaceMembers } from '~/swr-hooks/members/useWorkspaceMembers';
import { useGetExistingShortIdInformation } from '~/swr-hooks/shortUrl/useGetExistingShortIdInformation';
import { useGetShortIdForCanvas } from '~/swr-hooks/shortUrl/useGetShortIdForCanvas';
import { canCreateShortUrl, canEditShortUrl, canViewShortUrl } from '~/utils/permissions/shortUrlPermissions';
import { getCanvasPreviewSearchParams, getFullShareUrl } from '~/utils/shareUrl';
import { buildShareUrlCopiedAnalyticsParams } from '~/utils/shortUrl/buildShareUrlCopiedAnalyticsParams';

export type SharePrivateCanvasModalProps = AirModalProps<{
  canvasId: string;
  previewNodeId?: string;
  trackLocation: AirActionTrackingLocation;
}>;

export const SharePrivateCanvasModal = memo(
  ({ canvasId, onClose, previewNodeId, trackLocation }: SharePrivateCanvasModalProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });
    const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
    const canEdit = useMemo(() => canEditShortUrl(workspacePermissions), [workspacePermissions]);
    const canCreate = canCreateShortUrl(workspacePermissions);
    const { setToastXOffset, getToastXOffset, setToastRightOffset, getToastRightOffset } = useToasts();
    const [hasCreatedShareLinkInThisSession, setHasCreatedShareLinkInThisSession] = useState(false);

    const previousXOffset = useRef(getToastXOffset());
    const previousRightOffset = useRef(getToastRightOffset());

    useEffect(() => {
      const prevX = previousXOffset.current;
      const prevRight = previousRightOffset.current;
      setToastXOffset(0);
      setToastRightOffset(0);
      return () => {
        setToastXOffset(prevX);
        setToastRightOffset(prevRight);
      };
    }, [setToastXOffset, setToastRightOffset]);

    const { data: shortIdInfo, isLoading: isInitialLoading } = useGetExistingShortIdInformation({
      id: canvasId,
      type: ShortUrlObjectType.canvas,
      boardContext: undefined,
      canView: canViewShortUrl(workspacePermissions),
    });

    const {
      mutate: createShortIdForCanvas,
      isPending: isCreatingShortIdForCanvas,
      isError: isErrorCreatingShortIdForCanvas,
      reset: resetCreateShortIdForCanvas,
    } = useGetShortIdForCanvas();

    const hasShortId = !!shortIdInfo;

    const shareUrl =
      shortIdInfo && canEdit ? getFullShareUrl(shortIdInfo.id, getCanvasPreviewSearchParams(previewNodeId)) : undefined;

    const [showRegenerateModal] = useAirModal(SharePrivateCanvasRegenerate);
    const [showShareModal, hideShareModal] = useAirModal(SharePrivateCanvasModal);
    const { showTurnOnShareLinkNotifications } = useShowTurnOnShareLinkNotifications();

    const handleShareLinkCreatedFallback = useCallback(() => {
      showTurnOnShareLinkNotifications({
        onManageClick: onClose,
      });
    }, [onClose, showTurnOnShareLinkNotifications]);

    // The nudge panel itself is never rendered here — this hook is kept for its
    // "turn on share link notifications" fallback toast.
    useShowShareLinkTeamInviteNudgeForModal({
      isShareLinkCreated: hasCreatedShareLinkInThisSession,
      isShareLinkReady: hasShortId,
      location: 'canvas_share_link',
      onShareLinkCreatedFallback: handleShareLinkCreatedFallback,
    });

    const createShareLink = useCallback(() => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        return;
      }

      createShortIdForCanvas(
        { workspaceId, id: canvasId },
        {
          onSuccess: () => {
            setHasCreatedShareLinkInThisSession(true);
          },
        },
      );
    }, [canvasId, createShortIdForCanvas, currentWorkspace?.id]);

    useEffect(() => {
      if (
        !isInitialLoading &&
        !hasShortId &&
        canCreate &&
        !isCreatingShortIdForCanvas &&
        !isErrorCreatingShortIdForCanvas
      ) {
        createShareLink();
      }
    }, [
      canCreate,
      createShareLink,
      hasShortId,
      isCreatingShortIdForCanvas,
      isErrorCreatingShortIdForCanvas,
      isInitialLoading,
    ]);

    const handleRetryCreateShareLink = useCallback(() => {
      if (isCreatingShortIdForCanvas) {
        return;
      }

      resetCreateShortIdForCanvas();
      createShareLink();
    }, [createShareLink, isCreatingShortIdForCanvas, resetCreateShortIdForCanvas]);

    const { trackCopiedShareLink } = useTrackCopiedShareLink();
    const { members } = useWorkspaceMembers();
    const createdByName = useMemo(() => {
      const member = members?.allMembers.find((m) => m.accountId === shortIdInfo?.createdBy);

      return member ? formatFullName(member.firstName, member.lastName) || member.email || undefined : undefined;
    }, [members, shortIdInfo?.createdBy]);

    const handleRegenerate = useCallback(() => {
      if (!shortIdInfo) {
        return;
      }
      hideShareModal();

      const reopenShareModal = () => {
        showShareModal({
          canvasId,
          previewNodeId,
          trackLocation,
        });
      };

      showRegenerateModal({
        canvasId,
        trackLocation,
        shortIdInfo,
        onRegenerated: () => {
          reopenShareModal();
          showTurnOnShareLinkNotifications({
            onManageClick: hideShareModal,
          });
        },
        onCancel: reopenShareModal,
      });
    }, [
      shortIdInfo,
      hideShareModal,
      showRegenerateModal,
      canvasId,
      previewNodeId,
      trackLocation,
      showShareModal,
      showTurnOnShareLinkNotifications,
    ]);

    const handleCopied = useCallback(() => {
      if (!shortIdInfo) {
        return;
      }

      trackCopiedShareLink({
        id: canvasId,
        location: trackLocation,
        shareId: shortIdInfo.id,
        type: 'canvas',
        ...buildShareUrlCopiedAnalyticsParams({ objectType: 'canvas', shareId: shortIdInfo.id, shortUrl: shortIdInfo }),
      });
    }, [canvasId, shortIdInfo, trackCopiedShareLink, trackLocation]);

    const handlePreview = useCallback(() => window.open(shareUrl, NEW_TAB, 'noopener,noreferrer'), [shareUrl]);

    if (showingSubscriptionExpiredModal) {
      return null;
    }

    let content: ReactNode;

    if (isInitialLoading || !workspacePermissions || (!shortIdInfo && canCreate && !isErrorCreatingShortIdForCanvas)) {
      content = <SharePrivateContentSpinner />;
    } else if (!shortIdInfo && isErrorCreatingShortIdForCanvas && canCreate) {
      content = (
        <SharePrivateCreateLinkError isRetrying={isCreatingShortIdForCanvas} onRetry={handleRetryCreateShareLink} />
      );
    } else if (!shortIdInfo) {
      content = <NoPermissionToCreateShareLink />;
    } else if (!canEdit) {
      content = (
        <ReadonlyShareLink
          objectId={canvasId}
          objectType="canvas"
          searchParams={getCanvasPreviewSearchParams(previewNodeId)}
          shareId={shortIdInfo.id}
          shortIdInfo={shortIdInfo}
          trackLocation={trackLocation}
        />
      );
    } else {
      content = <SharePrivateCanvasModalBody canEdit={canEdit} canvasId={canvasId} shortIdInfo={shortIdInfo} />;
    }

    const headerSubtitle = shareUrl ? (
      <div className="flex min-w-0 items-center gap-4 pb-3">
        <a
          className="min-w-0 flex-1 truncate ads:text-metadata text-12 text-gray-9 ads:hover:text-secondary hover:text-gray-11 hover:underline"
          data-testid="PRIVATE_SHARE_LINK"
          href={shareUrl}
          rel="noopener noreferrer"
          target={NEW_TAB}
        >
          {shareUrl}
        </a>
        <DropdownMenu
          trigger={
            <IconButton
              adsIcon
              appearance="ghost"
              className="shrink-0"
              color="gray"
              data-testid="SHARE_LINK_MENU_BUTTON"
              icon={EllipsisVerticalIcon}
              label="Open menu"
              size="small"
            />
          }
        >
          {renderDropdownItems({
            options: [{ id: 'regenerate', label: 'Generate a new link', onSelect: handleRegenerate, type: 'item' }],
          })}
          {!!shortIdInfo?.createdAt && !!shortIdInfo.createdBy && (
            <ShareLinkMenuFooter createdAt={shortIdInfo.createdAt} createdByName={createdByName} />
          )}
        </DropdownMenu>
      </div>
    ) : undefined;

    return (
      <ShareLinkModal
        data-testid="SHARE_PRIVATE_CONTENT_MODAL"
        headerSubtitle={headerSubtitle}
        onClose={onClose}
        title="Share Canvas link"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto border-t ads:border-t-default border-t-gray-4 px-6 pt-4 pb-4">
          {content}
        </div>
        {shareUrl ? (
          <ShareLinkModalFooter>
            <div className="flex items-center justify-end gap-2">
              <Button
                appearance="filled"
                color="gray"
                data-testid="SHARE_LINK_PREVIEW_BUTTON"
                onClick={handlePreview}
                size="medium"
                type="button"
              >
                Preview
              </Button>
              <CopyLinkButton onCopied={handleCopied} shareUrl={shareUrl} />
            </div>
          </ShareLinkModalFooter>
        ) : null}
      </ShareLinkModal>
    );
  },
);

SharePrivateCanvasModal.displayName = 'SharePrivateCanvasModal';
